import { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { blogPosts, getBlogPost } from "../../../data/blog";
import { getBlogImage, getContentImage } from "../../../data/blogImages";
import "./blogPost.css";

function ArrowIcon({ direction = "right" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      style={direction === "left" ? { transform: "scaleX(-1)" } : undefined}
    >
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function prepareContent(html = "") {
  if (typeof DOMParser === "undefined") return { html, toc: [] };

  const doc = new DOMParser().parseFromString(html, "text/html");
  const used = new Set();
  const toc = [];

  // The page title is the only h1. Some articles carry their own h1 inside the
  // body (a section heading), so turn those into h2 before the contents list is built.
  doc.body.querySelectorAll("h1").forEach((h1) => {
    const h2 = doc.createElement("h2");
    h2.innerHTML = h1.innerHTML;
    Array.from(h1.attributes).forEach((attr) => h2.setAttribute(attr.name, attr.value));
    h1.replaceWith(h2);
  });

  doc.body.querySelectorAll("h2").forEach((heading, index) => {
    const title = heading.textContent.replace(/\s+/g, " ").trim();
    if (!title) return;

    let id = slugify(title) || `section-${index + 1}`;
    while (used.has(id)) id = `${id}-${index + 1}`;
    used.add(id);

    heading.id = id;
    toc.push({ id, title });
  });

  // Article images are referenced by key (e.g. "14a") and resolved to files in
  // src/assets/blogs. A missing file removes the image instead of loading a remote URL.
  doc.body.querySelectorAll("img").forEach((img) => {
    const local = getContentImage(img.getAttribute("src") || "");
    if (local) img.setAttribute("src", local);
    else img.remove();
  });

  return { html: doc.body.innerHTML, toc };
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPost(slug);

  const { html, toc } = useMemo(
    () => prepareContent(post?.content),
    [post]
  );

  useEffect(() => {
    if (!post) return;

    const previousTitle = document.title;
    document.title = post.seo?.title || post.title;

    let meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    if (post.seo?.description) meta.setAttribute("content", post.seo.description);

    return () => {
      document.title = previousTitle;
      if (previousDescription != null) {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, [post]);

  // Links inside the article HTML are plain <a> tags; route internal ones
  // through React Router so they don't trigger a full page reload.
  const handleBodyClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target.closest?.("a[href]");
    if (!anchor || anchor.target === "_blank") return;
    const href = anchor.getAttribute("href");
    if (href && href.startsWith("/") && !href.startsWith("//")) {
      event.preventDefault();
      navigate(href);
    }
  };

  const jumpTo = (event, id) => {
    event.preventDefault();
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!post) {
    return (
      <section className="bp-section">
        <div className="bp-container bp-notfound">
          <span className="bp-category">404</span>
          <h1 className="bp-title">Article not found</h1>
          <p className="bp-lead">
            The article you are looking for does not exist or may have moved.
          </p>
          <Link to="/resources/blogs" className="bp-back">
            <ArrowIcon direction="left" />
            Back to all articles
          </Link>
        </div>
      </section>
    );
  }

  const related = blogPosts
    .filter((item) => item.type === post.type && item.slug !== post.slug)
    .slice(0, 3);

  return (
    <article className="bp-section">
      <div className="bp-container">
        <header className="bp-header">
          <div className="bp-meta">
            <span className="bp-category">{post.category.toUpperCase()}</span>
            <span className="bp-meta-dot" aria-hidden="true" />
            <span className="bp-readtime">{post.readTime}</span>
          </div>

          <h1 className="bp-title">{post.title}</h1>

          {post.excerpt && (
            <p className="bp-lead">{post.excerpt.replace(/\s*\[\.\.\.\]$/, "...")}</p>
          )}
        </header>

        {getBlogImage(post) && (
          <figure className="bp-hero">
            <img src={getBlogImage(post)} alt={post.imageAlt} />
          </figure>
        )}

        <div className="bp-layout">
          <div
            className="bp-body"
            onClick={handleBodyClick}
            dangerouslySetInnerHTML={{ __html: html }}
          />

          <aside className="bp-sidebar">
            {toc.length > 1 && (
              <nav className="bp-card bp-toc" aria-label="In this article">
                <h2 className="bp-card-title">In this article</h2>
                <ol>
                  {toc.map((item) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`} onClick={(e) => jumpTo(e, item.id)}>
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            <div className="bp-card bp-cta">
              <h2 className="bp-card-title">Need quality training data?</h2>
              <p>
                Talk to our team about annotation for your AI project.
              </p>
              <Link to="/contact-us" className="bp-cta-btn">
                Get in touch
                <ArrowIcon />
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="bp-related" aria-labelledby="bp-related-title">
            <div className="bp-related-head">
              <h2 id="bp-related-title">Keep reading</h2>
              <Link to="/resources/blogs" className="bp-back">
                All articles
                <ArrowIcon />
              </Link>
            </div>

            <div className="bp-related-grid">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/resources/blogs/${item.slug}`}
                  className="bp-related-card"
                >
                  <div className="bp-related-img">
                    {getBlogImage(item) && (
                      <img
                        src={getBlogImage(item)}
                        alt={item.imageAlt}
                        loading="lazy"
                      />
                    )}
                  </div>
                  <span className="bp-category">
                    {item.category.toUpperCase()}
                  </span>
                  <h3>{item.title}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}
